import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerUserManual } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './Register.css';

function Register() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const selectedGender = localStorage.getItem('selectedGender');

    const [formData, setFormData] = useState({
        nickname: '',
        username: '',
        password: '',
        email: '',
        country: '',
        birthDate: '',
        profilePhoto: null,
        passportPhoto: null
    });

    const [previews, setPreviews] = useState({
        profilePhoto: null,
        passportPhoto: null
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showInfo, setShowInfo] = useState(selectedGender === 'female');

    if (!selectedGender) {
        navigate('/');
        return null;
    }

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        const fieldName = e.target.name;

        if (file) {
            setFormData({
                ...formData,
                [fieldName]: file
            });

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviews({
                    ...previews,
                    [fieldName]: reader.result
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Manual registration for both male and female users
            if (selectedGender === 'male') {
                if (!formData.nickname || !formData.username || !formData.password || !formData.birthDate || !formData.profilePhoto) {
                    throw new Error('Please fill all required fields');
                }

                const data = new FormData();
                data.append('nickname', formData.nickname);
                data.append('username', formData.username);
                data.append('password', formData.password);
                data.append('birthDate', formData.birthDate);
                data.append('gender', 'male');
                data.append('profilePhoto', formData.profilePhoto);
                
                if (formData.email) {
                    data.append('email', formData.email);
                }

                const response = await registerUserManual(data);
                login(response.user);
                navigate('/male-dashboard');
            } else {
                // Female users - manual registration
                if (!formData.nickname || !formData.username || !formData.password || !formData.birthDate || !formData.profilePhoto || !formData.country || !formData.passportPhoto) {
                    throw new Error('Please fill all required fields');
                }

                const data = new FormData();
                data.append('nickname', formData.nickname);
                data.append('username', formData.username);
                data.append('password', formData.password);
                data.append('birthDate', formData.birthDate);
                data.append('gender', 'female');
                data.append('profilePhoto', formData.profilePhoto);
                data.append('country', formData.country);
                data.append('passportPhoto', formData.passportPhoto);
                
                if (formData.email) {
                    data.append('email', formData.email);
                }

                const response = await registerUserManual(data);
                login(response.user);
                navigate('/waiting');
            }
        } catch (error) {
            console.error('Registration error:', error);
            setError(error.response?.data?.message || error.message || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="register-page">
            <div className="register-container">
                {showInfo && selectedGender === 'female' && (
                    <div className="info-modal">
                        <div className="info-content">
                            <h2>Earn Money with Next!</h2>
                            <div className="info-points">
                                <div className="info-point">
                                    <span className="icon">💰</span>
                                    <p>Earn money for every minute you spend chatting with male users</p>
                                </div>
                                <div className="info-point">
                                    <span className="icon">🏠</span>
                                    <p>Work from the comfort of your home, anytime you want</p>
                                </div>
                                <div className="info-point">
                                    <span className="icon">✨</span>
                                    <p>Your rate will be set by our admin team based on your profile</p>
                                </div>
                                <div className="info-point">
                                    <span className="icon">🔒</span>
                                    <p>Secure and safe platform with verified users only</p>
                                </div>
                            </div>
                            <button 
                                className="continue-btn"
                                onClick={() => setShowInfo(false)}
                            >
                                Continue Registration
                            </button>
                        </div>
                    </div>
                )}

                <div className="register-card">
                    <h1>Complete Your Profile</h1>
                    <p className="subtitle">
                        {selectedGender === 'male' 
                            ? 'Create your account' 
                            : 'Please provide the following information for verification'}
                    </p>

                    {error && (
                        <div className="error-message">{error}</div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Name (Nickname) *</label>
                            <input
                                type="text"
                                name="nickname"
                                value={formData.nickname}
                                onChange={handleInputChange}
                                required
                                placeholder="Enter your name"
                            />
                        </div>

                        <div className="form-group">
                            <label>Username *</label>
                            <input
                                type="text"
                                name="username"
                                value={formData.username}
                                onChange={handleInputChange}
                                required
                                placeholder="Choose a username"
                            />
                        </div>

                        <div className="form-group">
                            <label>Password *</label>
                            <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                required
                                placeholder="Choose a password"
                                minLength={6}
                            />
                        </div>

                        <div className="form-group">
                            <label>Email (Optional)</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="Enter your email (optional)"
                            />
                        </div>

                        <div className="form-group">
                            <label>Birth Date *</label>
                            <input
                                type="date"
                                name="birthDate"
                                value={formData.birthDate}
                                onChange={handleInputChange}
                                required
                                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                            />
                        </div>

                        <div className="form-group">
                            <label>Profile Photo *</label>
                            <div className="file-upload">
                                {previews.profilePhoto && (
                                    <img src={previews.profilePhoto} alt="Profile preview" className="preview" />
                                )}
                                <input
                                    type="file"
                                    name="profilePhoto"
                                    onChange={handleFileChange}
                                    accept="image/*"
                                    required={!previews.profilePhoto}
                                />
                            </div>
                        </div>

                        {selectedGender === 'female' && (
                            <>
                                <div className="form-group">
                                    <label>Country *</label>
                                    <input
                                        type="text"
                                        name="country"
                                        value={formData.country}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Enter your country"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Passport Photo *</label>
                                    <p className="help-text">
                                        Please hold your passport close to your face and take a selfie
                                    </p>
                                    <div className="file-upload">
                                        {previews.passportPhoto && (
                                            <img src={previews.passportPhoto} alt="Passport preview" className="preview" />
                                        )}
                                        <input
                                            type="file"
                                            name="passportPhoto"
                                            onChange={handleFileChange}
                                            accept="image/*"
                                            required
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        <button 
                            type="submit" 
                            className="submit-btn"
                            disabled={loading}
                        >
                            {loading ? 'Submitting...' : 'Complete Registration'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default Register;